import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { APITypes } from '../../primary-record-types';

const intl = getIntl('useFastenConnect', {
    errorConnecting: 'Error connecting to Fasten Health',
});

export type UseFastenConnectArgs = {
    medicalProfileId: string;
    onConnectUrlReceived: (redirectUrl: string) => void;
};

/** `apiMode` must be the mode the catalog entry was fetched in (FastenSearchSelection carries it). */
export type FastenConnectRequest = APITypes.ehrIntegrationsService.FastenConnectRequest;

export default function useFastenConnect({ medicalProfileId, onConnectUrlReceived }: UseFastenConnectArgs) {
    const notify = useNotify();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const isFocused = useIsFocused();

    // Reset redirecting state when user navigates back (e.g. browser back from OAuth page)
    useEffect(() => {
        if (isFocused && isRedirecting) {
            setIsRedirecting(false);
        }
    }, [isFocused]); // eslint-disable-line react-hooks/exhaustive-deps

    const connectMutation = useMutation({
        mutationFn: async (args: FastenConnectRequest) => {
            const response = await appAPI.post<{ redirectUrl: string }>(
                urls.api.ehrIntegrations.fasten.endpoints.connect(medicalProfileId),
                { body: args },
            );
            // On web the page navigates away so we keep the spinner forever.
            // On mobile the browser opens externally — the app stays mounted, so don't lock the UI.
            if (Platform.OS === 'web') setIsRedirecting(true);
            onConnectUrlReceived(response.redirectUrl);
        },
        onError: () => {
            notify('error', intl('errorConnecting'));
        },
    });

    return {
        connect: connectMutation.mutate,
        isConnecting: connectMutation.isPending || isRedirecting,
    };
}
